import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import './ChatComponent.css'
import {
  Box,
  Container,
  TextField,
  List,
  Chip,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  CircularProgress,
  IconButton,
  Card,
  CardActionArea,
  Grid,
  Button,
} from '@mui/material'
import small_logo from 'components/assets/small_logo.svg'
import {
  AttachFile,
  Close,
  Receipt,
  LocalShipping,
  Person,
  AttachMoney,
  ShoppingCart,
  History,
} from '@mui/icons-material'
import { GoogleGenerativeAI } from '@google/generative-ai'
import { useGetCustomersLazyQuery } from 'services/customers'
import {
  useGetInboundTicketsQuery,
  useGetInboundTicketsByIdsMutation,
  useUpdateInboundTicketMutation,
} from 'services/inboundTickets'
import { useGetMaterialTypesQuery } from 'services/materialTypes'
import { useCreatePriceSheetMutation } from 'services/priceSheets'
import { useCreatePurchaseOrderMutation, useGetPurchaseOrdersV2Query } from 'services/purchaseOrders'
import { styled } from '@mui/material/styles'
import { toolDefinitions, getSystemMessage, memoryUtils } from 'utils/sparkyTools'
import { useSelector } from 'react-redux'
import { getUnitConversionFactors } from 'utils/helpers'

// Constants
const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_KEY)
const model = genAI.getGenerativeModel({
  model: 'gemini-2.0-flash',
  tools: {
    functionDeclarations: toolDefinitions,
  },
})

// Utility Functions
const transformInboundTickets = tickets => {
  return tickets.map(ticket => ({
    uuid: ticket.id,
    ticketId: ticket.ticketId,
    customer: ticket.customer,
    transportationInfo: ticket.transportationInfo,
    status: ticket.status.value,
    truckWeight: ticket.truckWeight,
    freightCost: ticket.freightCost,
    effectiveDate: ticket.effectiveDate,
    materialNames: ticket.materialTypeCommonNames,
    materials:
      ticket.materials?.map(material => ({
        id: material.id,
        materialTypeId: material.materialTypeId,
        materialName: material.materialType?.commonName,
        netWeight: material.weightAndPrice?.netWeight?.commonString,
        deductions: material.deductions,
        netPrice: material.weightAndPrice?.netPrice?.commonString,
        netValue: material.weightAndPrice?.netValue?.commonString,
        weightAndPrice: {
          netPrice: material.weightAndPrice?.netPrice,
          netValue: material.weightAndPrice?.netValue,
          customerPreferredPrice: material.weightAndPrice?.customerPreferredPrice,
          netWeight: material.weightAndPrice?.netWeight,
        },
      })) || [],
    netValue: ticket.netValue,
    netWeight: ticket.netWeight,
    yardName: ticket.yard?.name || 'N/A',
  }))
}

const transformPurchaseOrders = purchaseOrders => {
  return purchaseOrders.map(po => ({
    id: po.id,
    purchaseOrderNumber: po.purchaseOrderTicketId,
    customerOrderNumber: po.customerSONumber,
    effectiveDate: po.effectiveDate,
    status: po.status,
    supplier: po.supplier,
    materials:
      po.priceEntries?.map(entry => ({
        materialName: entry.materialName,
        weightUnits: entry.weightUnits,
        targetWeight: entry.targetWeight,
      })) || [],
    fulfilledAmount: po.fulfilledAmount,
    archivedAt: po.archivedAt,
  }))
}

const retryApiCall = async (apiCallFn, maxRetries = 3, delay = 1000) => {
  let lastError

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await apiCallFn()
    } catch (error) {
      lastError = error

      // Check if this is a retryable error (service unavailable, rate limit, etc.)
      const isRetryable =
        error?.error?.code === 503 ||
        error?.error?.status === 'UNAVAILABLE' ||
        error?.message?.includes('unavailable') ||
        error?.message?.includes('rate limit')

      if (!isRetryable) {
        throw error // Don't retry non-retryable errors
      }

      console.log(`API call failed (attempt ${attempt + 1}/${maxRetries}). Retrying in ${delay}ms...`, error)

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay * (attempt + 1)))
    }
  }

  // If we've exhausted all retries, throw the last error
  throw lastError
}

// Styled Components
const SuggestionCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: '8px',
  transition: 'transform 0.4s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
  '& .MuiCardActionArea-root': {
    height: '100%',
    padding: theme.spacing(2),
  },
  '& .MuiTypography-root': {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 400,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 32,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}))

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  padding: theme.spacing(3),
  textAlign: 'center',
  '& img': {
    width: '200px',
    marginBottom: theme.spacing(3),
  },
}))

// Custom Markdown Renderers
const CustomParagraphRenderer = ({ children }) => <>{children}</>
CustomParagraphRenderer.propTypes = {
  children: PropTypes.node.isRequired,
}

const CustomLinkRenderer = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
)
CustomLinkRenderer.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
}

// Helper function to extract amount from price objects
// Moved to global scope to be accessible by calculateNetValue
const getAmount = (priceObj, isPriceField = false) => {
  if (typeof priceObj === 'number') {
    const num = Number(priceObj) // Ensure conversion
    return isNaN(num) ? 0 : num // Check for NaN
  }
  if (typeof priceObj === 'object' && priceObj !== null && priceObj.amount !== undefined) {
    // Check for amount property
    const amount = Number(priceObj.amount)
    if (isNaN(amount)) return 0 // Check for NaN after Number()

    // If this is a price field and has precision, adjust the amount
    if (isPriceField && typeof priceObj.precision === 'number' && !isNaN(priceObj.precision)) {
      // Check precision is valid number
      const precision = Number(priceObj.precision)
      if (precision >= 0) {
        // Ensure precision is non-negative
        return amount / Math.pow(10, precision)
      }
    }
    return amount // Return raw amount if no valid precision
  }
  return 0 // Default to 0 if not a number or valid object
}

// Helper function to calculate net value based on weight and price
// Updated to handle potential NaN values and missing/invalid units
const calculateNetValue = (netWeightInput, netPriceInput, weightUnit) => {
  const netWeight = getAmount(netWeightInput) // Uses the updated getAmount
  const netPrice = getAmount(netPriceInput, true) // Uses the updated getAmount

  if (isNaN(netWeight) || isNaN(netPrice)) {
    console.warn('Invalid number encountered in calculateNetValue input:', { netWeightInput, netPriceInput })
    return 0 // Return 0 if inputs are not valid numbers after getAmount
  }

  const conversionFactor = getUnitConversionFactors()[weightUnit]

  if (!weightUnit || typeof conversionFactor !== 'number' || isNaN(conversionFactor) || conversionFactor <= 0) {
    // Check factor is positive number
    console.warn(`Invalid or missing conversion factor for unit "${weightUnit}". Defaulting to direct multiplication.`)
    // Default to direct multiplication (assuming netPrice unit matches netWeight unit if conversion fails)
    const value = netWeight * netPrice
    return isNaN(value) ? 0 : value
  }

  // Convert weight to pounds first, then divide by the unit conversion factor
  // This gives us the number of units (e.g. number of CWT, number of tons, etc.)
  const units = netWeight / conversionFactor
  if (isNaN(units)) {
    console.warn('NaN encountered calculating units in calculateNetValue:', { netWeight, conversionFactor })
    return 0 // Return 0 if units calculation fails
  }

  const calculatedValue = units * netPrice
  if (isNaN(calculatedValue)) {
    console.warn('NaN encountered calculating final net value in calculateNetValue:', { units, netPrice })
    return 0 // Return 0 if final calculation fails
  }
  return calculatedValue
}

function ChatComponent() {
  // State Management
  const { user } = useSelector(state => state.auth)
  const { defaultYardUm } = useSelector(state => state.settings)
  const [messages, setMessages] = useState([])
  const [isTyping, setIsTyping] = useState(false)
  const [inputMessage, setInputMessage] = useState('')
  const [attachments, setAttachments] = useState([])
  const [activeDocuments, setActiveDocuments] = useState([])
  const [pendingToolUse, setPendingToolUse] = useState(null)
  const [suggestedResponses, setSuggestedResponses] = useState([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [shouldAutoSend, setShouldAutoSend] = useState(false)
  const [chatSession, setChatSession] = useState(null)

  // System Configuration
  const getInitializedSystemMessage = () => {
    const memories = memoryUtils.getMemories(user?.attributes?.sub)
    const systemMessage = getSystemMessage()

    // Add memories to the system message
    const memoriesContent = Object.entries(memories)
      .map(([type, memoriesOfType]) => {
        const memoryEntries = Object.entries(memoriesOfType)
          .map(([key, { value }]) => `${key}: ${value}`)
          .join('\n')
        return `\n\nCurrent ${type} memories:\n${memoryEntries}`
      })
      .join('\n')

    return {
      ...systemMessage,
      content: systemMessage.content + memoriesContent,
    }
  }

  // Refs
  const messagesEndRef = useRef(null)

  // Query Options State
  const [queryOptions, setQueryOptions] = useState({
    filter: {
      statuses: ['INCOMPLETE', 'PRICE_COMPLETE', 'APPROVED', 'SCALE_COMPLETE', 'PAID', 'TRUCK_IN_YARD'],
      sort: {
        field: 'effectiveDate',
        ascending: false,
      },
      date: '',
      endDate: '',
      search: '',
    },
    page: 1,
    pageSize: 3,
  })

  const [materialQueryOptions, setMaterialQueryOptions] = useState({
    filter: JSON.stringify({ search: '' }),
    pageSize: 5,
    page: 1,
  })

  const [purchaseOrderQueryOptions, setPurchaseOrderQueryOptions] = useState({
    filter: JSON.stringify({ search: '', statuses: ['Open'] }),
    pageSize: 3,
    page: 1,
  })

  // API Queries and Mutations
  const { data: inboundTicketList, isFetching } = useGetInboundTicketsQuery({
    ...queryOptions,
    filter: JSON.stringify(queryOptions.filter),
  })

  const [triggerGetCustomers, { data: customerList, isFetching: isFetchingCustomers }] = useGetCustomersLazyQuery()
  const { data: materialList, isFetching: isFetchingMaterials } = useGetMaterialTypesQuery(materialQueryOptions)
  const { data: purchaseOrderList, isFetching: isFetchingPurchaseOrders } =
    useGetPurchaseOrdersV2Query(purchaseOrderQueryOptions)
  const [createPriceSheet] = useCreatePriceSheetMutation()
  const [createPurchaseOrder] = useCreatePurchaseOrderMutation()
  const [updateInboundTicket] = useUpdateInboundTicketMutation()
  const [getInboundTicketsByIds] = useGetInboundTicketsByIdsMutation()

  // File Handling Functions
  const convertToBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = error => reject(error)
    })
  }

  const handleFileUpload = async event => {
    const files = Array.from(event.target.files)
    const base64Files = await Promise.all(files.map(convertToBase64))
    setAttachments([...attachments, ...base64Files])
    setActiveDocuments([...activeDocuments, ...base64Files])
    event.target.value = '' // Reset input
  }

  // Chat Message Processing Functions
  const handleSend = async () => {
    if (!inputMessage.trim() && attachments.length === 0) return

    const newMessage = {
      message: inputMessage,
      direction: 'outgoing',
      sender: 'user',
      attachments,
    }

    const newMessages = [...messages, newMessage]
    setMessages(newMessages)
    setIsTyping(true)
    setInputMessage('')
    setAttachments([])
    setSuggestedResponses([])
    await processMessageToAssistant(newMessages)
  }

  const processMessageToAssistant = async chatMessages => {
    if (!chatSession) return
    try {
      const lastMessage = chatMessages[chatMessages.length - 1]
      const parts = []

      // Add text part
      if (lastMessage.message) {
        parts.push(lastMessage.message)
      }

      // Add attachments as inlineData
      if (lastMessage.attachments?.length > 0) {
        for (const attachment of lastMessage.attachments) {
          const base64Data = attachment.split(',')[1]
          const mimeType = attachment.split(';')[0].split(':')[1]
          parts.push({
            inlineData: {
              data: base64Data,
              mimeType: mimeType,
            },
          })
        }
      }

      // Wrap both the message sending and response handling in retryApiCall
      const response = await retryApiCall(
        async () => {
          const result = await chatSession.sendMessage(parts)
          return await result.response
        },
        3,
        1000,
      )

      // Extract function calls from the response parts
      const responseParts = response.candidates?.[0]?.content?.parts || response.parts || []
      const functionCallParts = responseParts.filter(part => part.functionCall)
      const functionCalls = functionCallParts.map(part => part.functionCall)

      console.log('Extracted function calls:', functionCalls)

      if (functionCalls && functionCalls.length > 0) {
        // Handle regular function calls
        const pendingCalls = functionCalls.map(fc => {
          // Update local states if needed for queries
          switch (fc.name) {
            case 'get_customers':
              triggerGetCustomers({
                filter: JSON.stringify(fc.args.filter || {}),
                page: fc.args.page || 1,
                pageSize: fc.args.pageSize || 10,
              })
              break
            case 'get_inbound_tickets':
              setQueryOptions(prev => ({
                ...prev,
                filter: {
                  ...prev.filter,
                  ...fc.args.filter,
                },
                page: fc.args.page || 1,
                pageSize: fc.args.pageSize || 30,
              }))
              break
            case 'get_materials':
              setMaterialQueryOptions({
                filter: JSON.stringify(fc.args.filter || {}),
                page: fc.args.page || 1,
                pageSize: fc.args.pageSize || 5,
              })
              break
            case 'get_purchase_orders':
              setPurchaseOrderQueryOptions({
                filter: JSON.stringify(fc.args.filter),
                page: fc.args.page || 1,
                pageSize: fc.args.pageSize || 30,
              })
              break
            // For other functions, we do nothing except store them for processing in the next step
            default:
              break
          }
          return fc
        })

        setPendingToolUse({
          tool_use_id: Date.now().toString(),
          originalMessages: chatMessages,
          functionCalls: pendingCalls,
        })
        return
      }

      // If no function calls, add AI text response
      const textResponse =
        response.text?.() ||
        response.candidates?.[0]?.content?.parts?.find(part => part.text)?.text ||
        "I couldn't process that request."

      // Add the AI response to messages
      setMessages([
        ...chatMessages,
        {
          message: textResponse,
          sender: 'Sparky',
          searchUI: response.candidates?.[0]?.content?.parts?.find(part => part.searchUI)?.searchUI,
        },
      ])

      // Generate suggested responses based on the AI's message
      generateSuggestedResponses(textResponse, chatMessages)

      setIsTyping(false)
    } catch (error) {
      console.error('Error processing message:', error)
      setIsTyping(false)

      // Friendly error message mapping
      let friendlyMessage = 'Oops! I hit a small snag. '

      if (error?.error?.code === 503 || error?.error?.status === 'UNAVAILABLE') {
        friendlyMessage += "I'm taking a quick break to recharge. Please try again in a moment!"
      } else if (error?.message?.includes('rate limit')) {
        friendlyMessage += "I'm a bit overwhelmed with requests right now. Let's try again in a few seconds! ⏳"
      } else {
        friendlyMessage += 'Something unexpected happened. Would you mind trying that again?'
      }

      setMessages(prev => [
        ...prev,
        {
          message: friendlyMessage,
          sender: 'Sparky',
        },
      ])
      setSuggestedResponses(['Try again', 'Let me rephrase that', 'No problem, I can wait'])
    }
  }

  const generateSuggestedResponses = async (aiMessage = []) => {
    try {
      console.log('Generating suggestions for:', aiMessage.substring(0, 100) + '...')

      // Create a prompt focusing just on the latest AI message
      const suggestionsPrompt = `Based on this AI assistant message: "${aiMessage}"

Generate 3 short, helpful follow-up responses that a user might want to send in reply.
The responses should be natural continuations of the conversation and follow these rules:

1. If the AI presents multiple options or choices:
   - ALWAYS include suggestions for ALL options presented
   - Format them as direct selections (e.g. "Use option A" or "Let's go with the second choice")

2. If the AI asks a question:
   - Include clear, direct answers
   - Add follow-up clarifications if relevant

3. If the AI provides information:
   - Include requests for more details
   - Ask about specific aspects mentioned
   - Request practical next steps

Only provide suggestions within the capabilities of the AI, based on the system message: ${JSON.stringify(
        getInitializedSystemMessage(),
        null,
        2,
      )}
and the tools you have.

All suggestions should be:
- From the user's perspective
- 3-10 words long
- Action-oriented and specific
- Natural conversational tone

Format your response as a JSON array of strings.`

      // Create a separate model instance for suggestions
      const suggestionsModel = genAI.getGenerativeModel({
        model: 'gemini-2.0-flash',
      })

      // Use retry mechanism for generating suggestions
      const suggestionsResult = await retryApiCall(
        () => suggestionsModel.generateContent(suggestionsPrompt),
        3, // max retries
        1000, // base delay in ms
      )

      const suggestionsText = suggestionsResult.response.text()

      console.log('Raw suggestions response:', suggestionsText)

      // Parse the JSON response
      try {
        // Extract JSON array from the response text
        const jsonMatch = suggestionsText.match(/\[.*\]/s)
        if (jsonMatch) {
          const suggestions = JSON.parse(jsonMatch[0])
          console.log('Parsed suggestions:', suggestions)
          setSuggestedResponses(suggestions.slice(0, 3)) // Limit to 3 suggestions
        } else {
          console.warn('Could not extract JSON from suggestions response')
          // Fallback to some default suggestions if parsing fails
          setSuggestedResponses(['Can you explain more?', 'Show me an example', 'What other options are there?'])
        }
      } catch (parseError) {
        console.error('Error parsing suggestions:', parseError)
        // Fallback to some default suggestions if parsing fails
        setSuggestedResponses(['Can you explain more?', 'Show me an example', 'What other options are there?'])
      }
    } catch (error) {
      console.error('Error generating suggestions:', error)
      setSuggestedResponses([])
    }
  }

  const generateSuggestionsAfterToolUse = (aiMessage, messageHistory) => {
    // Only generate suggestions if we have a text response
    if (aiMessage && typeof aiMessage === 'string') {
      generateSuggestedResponses(aiMessage, messageHistory)
    }
  }

  const clearContext = () => {
    setActiveDocuments([])
  }

  const handleNewChat = () => {
    setMessages([])
    setInputMessage('')
    setAttachments([])
    setActiveDocuments([])
    setSuggestedResponses([])
    setPendingToolUse(null)
    setIsTyping(false)

    // Start a new chat session with memories
    const session = model.startChat()
    const initializedSystemMessage = getInitializedSystemMessage()
    session.sendMessage(initializedSystemMessage.content, { role: 'system' })
    setChatSession(session)
  }

  // Effects
  useEffect(() => {
    // Scroll effect
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isTyping, suggestedResponses])

  useEffect(() => {
    const session = model.startChat()
    const initializedSystemMessage = getInitializedSystemMessage()
    session.sendMessage(initializedSystemMessage.content, { role: 'system' })
    setChatSession(session)
  }, [])

  useEffect(() => {
    // Auto-send effect
    if (shouldAutoSend && inputMessage && inputMessage.length > 0) {
      handleSend()
      setShouldAutoSend(false)
    }
  }, [inputMessage, shouldAutoSend])

  useEffect(() => {
    // Tool use processing effect
    if (!pendingToolUse || isProcessing) return

    const handleToolResponse = async () => {
      setIsProcessing(true)
      try {
        // The functionCalls we are about to respond to
        const expectedFunctionCalls = pendingToolUse.functionCalls || []

        // 1) Execute each function call exactly once (existing code for non-multi-query)
        const results = await Promise.all(
          expectedFunctionCalls.map(async functionCall => {
            if (!functionCall || !functionCall.name) {
              console.error('Invalid function call:', functionCall)
              return {
                name: 'unknown',
                data: {
                  items: [],
                  totalResultsCount: 0,
                  error: 'Invalid function call structure',
                },
              }
            }

            switch (functionCall.name) {
              case 'get_inbound_tickets': {
                const transformedTickets = inboundTicketList ? transformInboundTickets(inboundTicketList.Items) : []
                return {
                  name: functionCall.name,
                  data: {
                    items: transformedTickets,
                    totalResultsCount: inboundTicketList?.Items?.length || 0,
                  },
                }
              }
              case 'get_inbound_tickets_by_ids': {
                // Call the mutation to get tickets by IDs
                try {
                  const response = await getInboundTicketsByIds({
                    body: functionCall.args.ticketIds,
                  }).unwrap()

                  // Transform the tickets if needed
                  const transformedTickets = response?.Items ? transformInboundTickets(response.Items) : []

                  return {
                    name: functionCall.name,
                    data: {
                      items: transformedTickets,
                      totalResultsCount: transformedTickets?.length || 0,
                    },
                  }
                } catch (error) {
                  console.error('Error fetching tickets by IDs:', error)
                  return {
                    name: functionCall.name,
                    data: {
                      items: [],
                      totalResultsCount: 0,
                      error: error.message || 'Failed to fetch tickets by IDs',
                    },
                  }
                }
              }
              case 'get_customers': {
                return {
                  name: functionCall.name,
                  data: {
                    items: customerList || [],
                    totalResultsCount: customerList?.length || 0,
                  },
                }
              }
              case 'get_materials': {
                return {
                  name: functionCall.name,
                  data: {
                    items: materialList || [],
                    totalResultsCount: materialList?.length || 0,
                  },
                }
              }
              case 'get_purchase_orders': {
                // Update the purchase order query options
                setPurchaseOrderQueryOptions({
                  filter: JSON.stringify(functionCall.args.filter),
                  page: functionCall.args.page || 1,
                  pageSize: functionCall.args.pageSize || 30,
                })

                // Wait for the query to complete
                await new Promise(resolve => setTimeout(resolve, 100))

                const transformedPurchaseOrders = purchaseOrderList?.Items
                  ? transformPurchaseOrders(purchaseOrderList.Items)
                  : []

                return {
                  name: functionCall.name,
                  data: {
                    items: transformedPurchaseOrders,
                    totalResultsCount: purchaseOrderList?.totalResultsCount || 0,
                  },
                }
              }
              case 'create_price_sheet': {
                const priceSheetResult = await createPriceSheet({
                  priceSheet: {
                    baseName: functionCall.args.baseName,
                    type: functionCall.args.type,
                    entriesObj: functionCall.args.entriesObj,
                  },
                }).unwrap()

                return {
                  name: functionCall.name,
                  data: {
                    items: [priceSheetResult],
                    totalResultsCount: 1,
                  },
                }
              }
              case 'create_purchase_order': {
                const purchaseOrderResult = await createPurchaseOrder({
                  purchaseOrderCreation: functionCall.args,
                }).unwrap()

                return {
                  name: functionCall.name,
                  data: {
                    items: [purchaseOrderResult],
                    totalResultsCount: 1,
                  },
                }
              }
              case 'update_inbound_ticket': {
                // First, get the ticket data using the ID
                try {
                  const ticketResponse = await getInboundTicketsByIds({
                    body: [functionCall.args.ticketId],
                  }).unwrap()

                  const ticketData = ticketResponse?.Items?.[0]

                  if (!ticketData) {
                    throw new Error(`Ticket with ID ${functionCall.args.ticketId} not found`)
                  }

                  // Helper function to preserve existing values
                  const preserveValue = (newValue, existingValue) => {
                    // If newValue is undefined or null, preserve the existing value.
                    // Allow 0 as a valid override.
                    if (newValue === undefined || newValue === null) {
                      return existingValue
                    }
                    return newValue
                  }

                  // Update materials with new prices if provided
                  const updatedMaterials = ticketData.materials.map(material => {
                    const updateMaterial = functionCall.args.materials?.find(m => m.id === material.id)
                    if (updateMaterial) {
                      const updatedWeightAndPrice = updateMaterial.weightAndPrice

                      const newGrossAmount = updatedWeightAndPrice?.gross?.amount
                      const newTareAmount = updatedWeightAndPrice?.tare?.amount

                      // Get price values with proper precision handling
                      const netPrice = getAmount(updatedWeightAndPrice?.netPrice, true)
                      const netWeight = getAmount(updatedWeightAndPrice?.netWeight)

                      // Calculate net value based on price, weight, and unit
                      const materialNetValue = calculateNetValue(
                        netWeight,
                        netPrice,
                        material.weightAndPrice?.customerPreferredUm,
                      )

                      // Get the original precision values
                      const netPricePrecision =
                        updatedWeightAndPrice?.netPrice?.precision ?? material.weightAndPrice?.netPrice?.precision ?? 4

                      return {
                        id: material.id,
                        materialTypeId: material.materialTypeId,
                        code: material.materialType?.code,
                        weightAndPrice: {
                          customerPreferredUm: material.weightAndPrice?.customerPreferredUm,
                          // Use the calculated netPrice and its precision for customerPreferredPrice
                          customerPreferredPrice: {
                            amount: Math.round(netPrice * Math.pow(10, netPricePrecision)),
                            precision: netPricePrecision,
                          },
                          gross: {
                            amount:
                              newGrossAmount !== undefined && newGrossAmount !== 0
                                ? newGrossAmount
                                : material.weightAndPrice?.gross?.amount ?? 0,
                          },
                          tare: {
                            amount:
                              newTareAmount !== undefined && newTareAmount !== 0
                                ? newTareAmount
                                : material.weightAndPrice?.tare?.amount ?? 0,
                          },
                          deductions: updatedWeightAndPrice?.deductions ?? [],
                          netWeight: {
                            amount: netWeight,
                          },
                          um: material.weightAndPrice?.um,
                          netPrice: {
                            amount: Math.round(netPrice * Math.pow(10, netPricePrecision)),
                            precision: netPricePrecision,
                          },
                          netValue: {
                            amount: materialNetValue,
                            precision: netPricePrecision,
                          },
                          isManualGross: false,
                          isManualTare: false,
                        },
                        grossImages: material.status.grossImages || [],
                        tareImages: material.status.tareImages || [],
                        rowIndex: material.rowIndex,
                        tareWeightRequired: material.tareWeightRequired ?? false,
                        axleWeights: material.axleWeights || [],
                      }
                    }
                    return material
                  })

                  // Calculate total net value from all materials
                  const totalNetValue = updatedMaterials.reduce((sum, material) => {
                    // Sum using the raw netValue amount calculated for each material
                    const materialValue = getAmount(material.weightAndPrice?.netValue)
                    return sum + materialValue
                  }, 0)

                  // Format taxRate to string with 3 decimal places
                  const formatTaxRate = rate => {
                    if (typeof rate === 'number') {
                      return rate.toFixed(3)
                    }
                    return rate
                  }

                  // Construct the inboundTicketSave object
                  const inboundTicketSave = {
                    customer: {
                      id: ticketData.customer.id,
                    },
                    passengers: ticketData.numberOfPassengers,
                    effectiveDate: ticketData.effectiveDate,
                    transportationInfo: {
                      carrier: ticketData.transportationInfo.carrier,
                      carrierNumber: ticketData.transportationInfo.carrierNumber,
                      trailerNumber: ticketData.transportationInfo.trailerNumber,
                      purchaseOrderId: preserveValue(
                        functionCall.args.transportationInfo?.purchaseOrderId,
                        ticketData.transportationInfo.purchaseOrderId,
                      ),
                      purchaseOrderNumber: preserveValue(
                        functionCall.args.transportationInfo?.purchaseOrderNumber,
                        ticketData.transportationInfo.purchaseOrderNumber,
                      ),
                    },
                    status: {
                      value: functionCall.args.status,
                      date: new Date().toISOString(),
                      userId: ticketData.status.userId,
                      image: '',
                      imageIsTemp: false,
                      tareImage: '',
                      tareImageIsTemp: false,
                    },
                    statusHistory: ticketData.statusHistory || [],
                    truckWeight: {
                      gross: {
                        amount: ticketData.truckWeight?.gross?.amount ?? 0,
                        units: ticketData.truckWeight?.gross?.units || defaultYardUm,
                      },
                      tare: {
                        amount: ticketData.truckWeight?.tare?.amount ?? 0,
                        units: ticketData.truckWeight?.tare?.units || defaultYardUm,
                      },
                      net: {
                        amount: ticketData.truckWeight?.net?.amount ?? 0,
                        units: ticketData.truckWeight?.net?.units || defaultYardUm,
                      },
                    },
                    freightCost: {
                      cost: Number(ticketData.freightCost.cost),
                      chargeBack: Number(ticketData.freightCost.chargeBack),
                      userSet: ticketData.freightCost.userSet,
                      userChargebackSet: ticketData.freightCost.userChargebackSet,
                      id: ticketData.freightCost.id,
                    },
                    id: ticketData.id,
                    loadWeight: {
                      gross: preserveValue(
                        getAmount(functionCall.args.loadWeight?.gross),
                        ticketData.truckWeight?.gross ?? { amount: 0, units: defaultYardUm },
                      ),
                      tare: preserveValue(
                        getAmount(functionCall.args.loadWeight?.tare),
                        ticketData.truckWeight?.tare ?? { amount: 0, units: defaultYardUm },
                      ),
                      deductions: preserveValue(
                        getAmount(functionCall.args.loadWeight?.deductions),
                        ticketData.truckWeight?.deductions ?? 0, // Assuming deductions is a number or getAmount handles its structure
                      ),
                      net: getAmount(functionCall.args.loadWeight?.net ?? ticketData.truckWeight?.net?.amount ?? 0),
                    },
                    netWeight: getAmount(functionCall.args.netWeight ?? ticketData.netWeight ?? 0),
                    netValue: isNaN(totalNetValue) ? 0 : totalNetValue,
                    feeOrPromotion: ticketData.feeOrPromotion || [],
                    hash: ticketData.hash,
                    materials: updatedMaterials,
                    customerId: ticketData.customer.id,
                    contactId: ticketData.contactId,
                    docs: ticketData.docs || [],
                    axleWeights: ticketData.axleWeights || [],
                    scrapVehicles: ticketData.scrapVehicles || [],
                    ticketId: ticketData.ticketId,
                    carrierId: ticketData.carrierId,
                    taxRate: formatTaxRate(functionCall.args.taxRate ?? ticketData.taxRate),
                    vehicleId: ticketData.vehicleId,
                    locationId: ticketData.locationId,
                  }

                  const result = await updateInboundTicket({
                    ticketId: functionCall.args.ticketId,
                    inboundTicketSave,
                  }).unwrap()

                  return {
                    name: functionCall.name,
                    data: {
                      items: [result],
                      totalResultsCount: 1,
                    },
                  }
                } catch (error) {
                  console.error('Error updating inbound ticket:', error)
                  return {
                    name: functionCall.name,
                    data: {
                      items: [],
                      totalResultsCount: 0,
                      error: error.message || 'Failed to update ticket',
                    },
                  }
                }
              }
              case 'updateMemory': {
                const { action, key, value, type } = functionCall.args || {}
                // Add userId to the function call
                functionCall.args = {
                  ...functionCall.args,
                  userId: user?.attributes?.sub,
                }
                if (action === 'store') {
                  memoryUtils.storeMemory(user?.attributes?.sub, key, value, type)
                  return {
                    name: functionCall.name,
                    data: {
                      success: true,
                      message: 'Memory stored successfully',
                    },
                  }
                } else if (action === 'retrieve') {
                  const memory = memoryUtils.retrieveMemory(user?.attributes?.sub, key, type)
                  return {
                    name: functionCall.name,
                    data: {
                      value: memory,
                      exists: memory !== null,
                    },
                  }
                } else if (action === 'delete') {
                  memoryUtils.clearMemory(user?.attributes?.sub, key, type)
                  return {
                    name: functionCall.name,
                    data: {
                      success: true,
                      message: 'Memory deleted successfully',
                    },
                  }
                }
                return {
                  name: functionCall.name,
                  data: {
                    success: false,
                    error: 'Invalid memory action',
                  },
                }
              }
              case 'search_web': {
                // Create a new Gemini instance with search enabled
                const searchModel = genAI.getGenerativeModel({
                  model: 'gemini-2.0-flash',
                  tools: {
                    googleSearch: {},
                  },
                })

                const searchResult = await searchModel.generateContent({
                  contents: [
                    {
                      role: 'user',
                      parts: [{ text: functionCall.args.query }],
                    },
                  ],
                })

                // Extract search results and grounding information
                const groundingMetadata = searchResult.response.candidates?.[0]?.groundingMetadata

                // Map grounding chunks to sources
                const sources =
                  groundingMetadata?.groundingChunks?.map(chunk => ({
                    title: chunk.web?.title,
                    url: chunk.web?.uri,
                  })) || []

                // Map grounding supports to structured content
                const supportedContent =
                  groundingMetadata?.groundingSupports?.map(support => ({
                    text: support.segment.text,
                    sources: support.groundingChunkIndices.map(index => sources[index]),
                    confidence: support.confidenceScores?.[0],
                  })) || []

                return {
                  name: functionCall.name,
                  data: {
                    items: [
                      {
                        text: searchResult.response.text(),
                        supportedContent,
                        sources,
                        searchQueries: groundingMetadata?.webSearchQueries || [],
                        searchUI: groundingMetadata?.searchEntryPoint?.renderedContent || '',
                      },
                    ],
                    totalResultsCount: sources.length,
                  },
                }
              }
              default:
                // If unrecognized function
                return {
                  name: functionCall.name,
                  data: {
                    items: [],
                    totalResultsCount: 0,
                    error: 'Unrecognized function name',
                  },
                }
            }
          }),
        )

        // 2) Build function responses with error checking
        const functionResponses = expectedFunctionCalls.map((functionCall, index) => {
          const result = results[index]
          if (!result) {
            return {
              functionResponse: {
                name: functionCall?.name || 'unknown',
                response: {
                  items: [],
                  totalResultsCount: 0,
                  error: 'Function execution failed',
                },
              },
            }
          }
          return {
            functionResponse: {
              name: result.name,
              response: {
                items: result?.data?.items || [],
                totalResultsCount: result?.data?.totalResultsCount || 0,
                ...(result?.data?.error && { error: result.data.error }),
                ...(result?.data?.success !== undefined && { success: result.data.success }),
                ...(result?.data?.message && { message: result.data.message }),
                ...(result?.data?.value !== undefined && { value: result.data.value }),
                ...(result?.data?.exists !== undefined && { exists: result.data.exists }),
              },
            },
          }
        })

        // 3) Send them as a single message and wait for response
        const result = await retryApiCall(
          () => chatSession.sendMessage(functionResponses),
          3, // max retries
          1000, // base delay in ms
        )
        const response = await result.response

        // 4) Add the model's textual reply
        const textResponse = response.text()
        const updatedMessages = [
          ...pendingToolUse.originalMessages,
          {
            message: textResponse,
            sender: 'Sparky',
            searchUI: results.find(r => r.name === 'search_web')?.data?.items?.[0]?.searchUI,
          },
        ]

        // 5) Check for nested function calls
        const nestedFunctionCalls = response.functionCalls()
        if (nestedFunctionCalls && nestedFunctionCalls.length > 0) {
          // Update messages first
          setMessages(updatedMessages)

          // Force state update to complete before setting new pending tool use
          await new Promise(resolve => setTimeout(resolve, 0))

          // Then set the new pending tool use
          const newPendingToolUse = {
            tool_use_id: Date.now().toString(),
            originalMessages: updatedMessages,
            functionCalls: nestedFunctionCalls,
          }
          setPendingToolUse(newPendingToolUse)
          setIsTyping(true)
        } else {
          // No new calls, finalize
          setMessages(updatedMessages)
          setPendingToolUse(null)
          setIsTyping(false)

          // Generate suggestions after tool use is complete
          generateSuggestionsAfterToolUse(textResponse, updatedMessages)
        }
      } catch (error) {
        setMessages(prev => [
          ...prev,
          {
            message: `Error processing request: ${error.message}`,
            sender: 'Sparky',
          },
        ])
        setPendingToolUse(null)
        setIsTyping(false)
      } finally {
        setIsProcessing(false)
      }
    }

    // Only process if all data is ready and we have a chat session
    const canProcess = !isFetching && !isFetchingCustomers && !isFetchingMaterials && !!chatSession

    if (canProcess) {
      handleToolResponse()
    }
  }, [
    pendingToolUse,
    isProcessing,
    isFetching,
    isFetchingCustomers,
    isFetchingMaterials,
    chatSession,
    customerList,
    materialList,
    setMessages,
    setPendingToolUse,
    setIsTyping,
    createPriceSheet,
    createPurchaseOrder,
    updateInboundTicket,
    getInboundTicketsByIds,
  ])

  // Render
  return (
    <Container maxWidth="false" disableGutters>
      <Box display="flex" flexDirection="column" height="75vh">
        <Box flexGrow={1} mb={2} overflow="auto">
          {messages.length === 0 ? (
            <EmptyStateContainer>
              <img src={small_logo} alt="Sparky Logo" style={{ width: '100px' }} />
              <Typography variant="h4" gutterBottom color="text.primary">
                Hello! I'm Sparky, your chatbot assistant
              </Typography>
              <Typography variant="subtitle1" gutterBottom color="text.secondary">
                Here are some things you can ask me:
              </Typography>
              <Grid container spacing={2} sx={{ mt: 2, px: 2 }}>
                {[
                  {
                    icon: <Receipt />,
                    text: "Show me this week's incomplete tickets",
                    message: "Can you show me this week's incomplete tickets?",
                  },
                  {
                    icon: <LocalShipping />,
                    text: 'What tickets are in the yard right now?',
                    message: 'What tickets are currently in truck in yard status?',
                  },
                  {
                    icon: <Person />,
                    text: 'Find tickets for customer',
                    message: 'Can you help me find tickets for a specific customer?',
                  },
                  {
                    icon: <History />,
                    text: 'Last customer visit',
                    message: 'Can you help me find the last time a customer was here?',
                  },
                  {
                    icon: <AttachMoney />,
                    text: 'Show me paid tickets from last week',
                    message: 'Show me all paid tickets from last week',
                  },
                  {
                    icon: <ShoppingCart />,
                    text: 'Create a purchase order',
                    message: 'I need help creating a new purchase order',
                  },
                ].map((suggestion, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <SuggestionCard elevation={2}>
                      <CardActionArea
                        onClick={() => {
                          setInputMessage(suggestion.message)
                          setShouldAutoSend(true)
                        }}
                      >
                        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                          {suggestion.icon}
                          <Typography variant="body2">{suggestion.text}</Typography>
                        </Box>
                      </CardActionArea>
                    </SuggestionCard>
                  </Grid>
                ))}
              </Grid>
            </EmptyStateContainer>
          ) : (
            <List>
              {messages.map((message, i) => (
                <ListItem key={i}>
                  <Box
                    display="flex"
                    alignItems="flex-end"
                    width="100%"
                    textAlign={message.sender === 'user' ? 'right' : 'left'}
                  >
                    {message.sender === 'Sparky' && (
                      <ListItemAvatar>
                        <Avatar src={small_logo} />
                      </ListItemAvatar>
                    )}
                    <ListItemText
                      primary={
                        <Box>
                          <Chip
                            label={
                              <span
                                style={{
                                  wordBreak: 'break-word',
                                  whiteSpace: 'pre-wrap',
                                  width: '100%',
                                }}
                              >
                                <div className="markdown-table">
                                  <ReactMarkdown
                                    components={{
                                      p: CustomParagraphRenderer,
                                      a: CustomLinkRenderer,
                                      br: ({ node, ...props }) => <br {...props} />,
                                    }}
                                    remarkPlugins={[remarkGfm]}
                                    whiteSpace="pre-wrap"
                                    breaks={true}
                                    unwrapDisallowed={true}
                                  >
                                    {message.message.replace(/\u003cbr\u003e/g, '\n')}
                                  </ReactMarkdown>
                                </div>
                                {message.attachments?.map((attachment, index) => (
                                  <img
                                    key={index}
                                    src={attachment}
                                    alt={`Uploaded ${index}`}
                                    style={{ maxWidth: '200px', margin: '5px' }}
                                  />
                                ))}
                                {message.searchUI && (
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: message.searchUI.replace(
                                        /<a class="chip"/g,
                                        '<a class="chip" target="_blank" rel="noopener noreferrer"',
                                      ),
                                    }}
                                    style={{ marginTop: '10px' }}
                                  />
                                )}
                              </span>
                            }
                            color={message.sender === 'Sparky' ? 'primary' : 'default'}
                            variant="filled"
                            style={{
                              maxWidth: '90%',
                              height: 'fit-content',
                              paddingTop: '8px',
                              paddingBottom: '8px',
                            }}
                          />
                          {message.message.toLowerCase().includes('error:') && (
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              onClick={() => {
                                const lastUserMessage = [...messages].reverse().find(msg => msg.sender === 'user')
                                if (lastUserMessage) {
                                  setMessages(messages.filter((_, index) => index !== i))
                                  processMessageToAssistant([...messages.filter((_, index) => index !== i)])
                                }
                              }}
                              style={{ marginTop: '8px' }}
                            >
                              Try Again
                            </Button>
                          )}
                        </Box>
                      }
                    />
                  </Box>
                </ListItem>
              ))}
              {isTyping && (
                <ListItem>
                  <ListItemText>
                    <Box display="flex" alignItems="center">
                      <CircularProgress size={20} />
                      <Typography variant="body2" paddingLeft={1} color="text.primary">
                        Sparky is working...
                      </Typography>
                    </Box>
                  </ListItemText>
                </ListItem>
              )}
              <div ref={messagesEndRef} />
            </List>
          )}
        </Box>

        {attachments.length > 0 && (
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {attachments.map((attachment, index) => (
              <Box key={index} position="relative">
                <img
                  src={attachment}
                  alt={`Preview ${index}`}
                  style={{
                    height: '100px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                  }}
                />
                <IconButton
                  size="small"
                  style={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: 'white',
                  }}
                  onClick={() => {
                    const newAttachments = [...attachments]
                    newAttachments.splice(index, 1)
                    setAttachments(newAttachments)
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {activeDocuments.length > 0 && (
          <Box mb={2}>
            <Typography variant="caption" color="textSecondary">
              Active Documents: {activeDocuments.length}
            </Typography>
            <IconButton size="small" onClick={clearContext}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        )}
        {/* Suggested responses - make sure they're visible */}
        {suggestedResponses.length > 0 && (
          <Box display="flex" flexWrap="wrap" gap={1} mb={2} justifyContent="center">
            {suggestedResponses.map((suggestion, index) => (
              <Chip
                key={index}
                label={suggestion}
                onClick={() => {
                  setInputMessage(suggestion)
                  setShouldAutoSend(true)
                }}
                clickable
                color="primary"
                variant="outlined"
                sx={{ margin: '4px', fontWeight: 'medium' }}
              />
            ))}
          </Box>
        )}
        <Box display="flex" alignItems="center">
          <input
            type="file"
            multiple
            accept="image/*,application/pdf"
            style={{ display: 'none' }}
            id="file-upload"
            onChange={handleFileUpload}
          />
          <label htmlFor="file-upload">
            <IconButton component="span">
              <AttachFile />
            </IconButton>
          </label>
          <TextField
            fullWidth
            autoFocus
            variant="outlined"
            placeholder="Type message here"
            value={inputMessage}
            onChange={e => setInputMessage(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSend()
              }
            }}
            multiline
            maxRows={4}
            minRows={1}
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={handleNewChat}
            sx={{ ml: 1, height: 56, minWidth: 100, width: 150 }}
          >
            New Chat
          </Button>
        </Box>
      </Box>
    </Container>
  )
}

export default ChatComponent
